/**
 * GuestPhotosSection Component
 * Photo management for guest project view (before/progress/after)
 */
import React, { useRef, useState } from 'react';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { Camera, Plus, X, Image as ImageIcon } from 'lucide-react';
import { PhotoModel } from '../../../types/protocols';
import { PhotoType } from '../../../types/guestAccess';
import { useGuestThumbnail } from '../hooks/useGuestThumbnail';
import ConfirmationModal from '../../../Components/ConfirmationModal';
import PhotoViewerModal from '../../../Components/PhotoViewerModal';
import { WORK_ORDER_LABELS } from '../../WorkOrders/workOrderSections';
import { groupByYear, orderPhotos } from '../../../Helpers/photoOrdering';
import PhotoYearHeading from '../../../Components/PhotoYearHeading';

interface GuestPhotosSectionProps {
    beforePhotos: { [key: string]: PhotoModel } | undefined;
    progressPhotos: { [key: string]: PhotoModel } | undefined;
    afterPhotos: { [key: string]: PhotoModel } | undefined;
    externalUserId: string;
    onUploadPhoto: (file: File, photoType: PhotoType) => Promise<void>;
    onDeletePhoto: (photoType: PhotoType, key: string) => Promise<void>;
    onGetDownloadUrl: (storagePath: string) => Promise<string>;
    submitting: boolean;
    uploadingPhotoType: PhotoType | null;
    canDelete: (authorId: string) => boolean;
    /** Completed project: everything is shown, nothing can be added or changed. */
    readOnly?: boolean;
}

interface DeleteConfirmState {
    show: boolean;
    photoType: PhotoType | null;
    photoKey: string | null;
}

interface PhotoGroupProps {
    title: string;
    photoType: PhotoType;
    photos: { [key: string]: PhotoModel } | undefined;
    /** Absent: no add tile (read-only). */
    onUpload?: (file: File) => void;
    onDeleteClick: (key: string) => void;
    onViewClick: (fullSizePath: string) => void;
    onGetDownloadUrl: (storagePath: string) => Promise<string>;
    submitting: boolean;
    isUploading: boolean;
    canDelete: (authorId: string) => boolean;
}

/**
 * Single photo in the grid. Loads the small (200x200) thumbnail for the tile —
 * the full-size original is only fetched on click (see onViewClick). Thumbnails
 * are generated asynchronously on upload, so a just-uploaded photo may not have
 * one yet; in that case the <img> load fails and we fall back to the full-size
 * original once.
 */
const GuestPhoto: React.FC<{
    photo: PhotoModel & { key: string };
    onGetDownloadUrl: (storagePath: string) => Promise<string>;
    onDeleteClick: (key: string) => void;
    onViewClick: (fullSizePath: string) => void;
    canDelete: boolean;
    submitting: boolean;
}> = ({ photo, onGetDownloadUrl, onDeleteClick, onViewClick, canDelete, submitting }) => {
    const { url, loading, error, onImgError } = useGuestThumbnail(
        photo.storagePath,
        onGetDownloadUrl
    );

    const handleClick = () => {
        if (url && !loading && !error) {
            // The section fetches the full-size original on demand and shows a
            // loading bar until it arrives — the thumbnail is never blown up.
            onViewClick(photo.storagePath);
        }
    };

    return (
        <div
            className="guest-photo-item position-relative"
            style={{ width: 100, height: 100 }}
        >
            {loading ? (
                <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light rounded">
                    <Spinner animation="border" size="sm" />
                </div>
            ) : error ? (
                <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light rounded">
                    <ImageIcon size={24} className="text-muted" />
                </div>
            ) : (
                <img
                    src={url || ''}
                    alt=""
                    className="rounded w-100 h-100"
                    style={{ objectFit: 'cover', cursor: 'pointer' }}
                    onClick={handleClick}
                    onError={onImgError}
                />
            )}
            {canDelete && (
                <button
                    className="guest-photo-delete btn btn-sm position-absolute"
                    style={{
                        top: 4,
                        right: 4,
                        padding: '2px 6px',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        border: 'none',
                        borderRadius: '50%',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDeleteClick(photo.key);
                    }}
                    disabled={submitting}
                >
                    <X size={14} color="white" />
                </button>
            )}
        </div>
    );
};

const PhotoGroup: React.FC<PhotoGroupProps> = ({
    title,
    photoType,
    photos,
    onUpload,
    onDeleteClick,
    onViewClick,
    onGetDownloadUrl,
    submitting,
    isUploading,
    canDelete,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Newest first, under year headings when the photos span more than one year
    const yearGroups = groupByYear(orderPhotos(photos ?? {}));

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            onUpload?.(files[0]);
        }
        // Reset input so same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const addTile = onUpload && (
        <label
            className="guest-photo-add d-flex align-items-center justify-content-center border rounded"
            style={{
                width: 100,
                height: 100,
                cursor: isUploading ? 'not-allowed' : 'pointer',
                borderStyle: 'dashed',
                opacity: isUploading ? 0.5 : 1,
            }}
        >
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleFileChange}
                disabled={isUploading}
            />
            {isUploading ? (
                <Spinner animation="border" size="sm" />
            ) : (
                <Plus size={24} className="text-muted" />
            )}
        </label>
    );

    return (
        <div className="guest-photos-group mb-4">
            <h6 className="text-muted mb-2">{title}</h6>
            {yearGroups.map(({ year, items }, groupIndex) => (
                <div key={year ?? ''} className={year ? 'mb-2' : undefined}>
                    {year && <PhotoYearHeading year={year} />}
                    <div className="guest-photos-grid d-flex flex-wrap gap-2">
                        {items.map((photo) => (
                            <GuestPhoto
                                key={photo.key}
                                photo={photo}
                                onGetDownloadUrl={onGetDownloadUrl}
                                onDeleteClick={onDeleteClick}
                                onViewClick={onViewClick}
                                canDelete={canDelete(photo.author)}
                                submitting={submitting}
                            />
                        ))}
                        {groupIndex === yearGroups.length - 1 && addTile}
                    </div>
                </div>
            ))}
        </div>
    );
};

const GuestPhotosSection: React.FC<GuestPhotosSectionProps> = ({
    beforePhotos,
    progressPhotos,
    afterPhotos,
    externalUserId,
    onUploadPhoto,
    onDeletePhoto,
    onGetDownloadUrl,
    submitting,
    uploadingPhotoType,
    canDelete,
    readOnly = false,
}) => {
    const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
        show: false,
        photoType: null,
        photoKey: null,
    });
    const [viewerUrl, setViewerUrl] = useState<string | null>(null);
    const [viewerLoading, setViewerLoading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    // Identifies the current viewer open request so a full-size fetch that
    // resolves after the user closes (or opens another photo) is ignored.
    const viewRequestRef = useRef(0);

    const handleViewPhoto = async (fullSizePath: string) => {
        const requestId = ++viewRequestRef.current;
        // Open the viewer showing a loading bar, then reveal the full-size
        // original once it's downloaded on demand (never the blurry thumbnail).
        setViewerUrl(null);
        setViewerLoading(true);
        try {
            const fullUrl = await onGetDownloadUrl(fullSizePath);
            if (viewRequestRef.current === requestId) {
                setViewerUrl(fullUrl);
                setViewerLoading(false);
            }
        } catch {
            // Full-size fetch failed — close the viewer.
            if (viewRequestRef.current === requestId) {
                setViewerLoading(false);
            }
        }
    };

    const handleUpload = async (file: File, photoType: PhotoType) => {
        setUploadError(null);
        try {
            await onUploadPhoto(file, photoType);
        } catch (err) {
            console.error('Error uploading photo:', err);
            const errorMessage = err instanceof Error ? err.message : 'Kunde inte ladda upp bild';
            setUploadError(errorMessage);
        }
    };

    const handleCloseViewer = () => {
        // Invalidate any in-flight full-size fetch so it can't reopen the viewer.
        viewRequestRef.current++;
        setViewerUrl(null);
        setViewerLoading(false);
    };

    const totalPhotos =
        Object.keys(beforePhotos || {}).length +
        Object.keys(progressPhotos || {}).length +
        Object.keys(afterPhotos || {}).length;

    const handleDeleteClick = (photoType: PhotoType, key: string) => {
        setDeleteConfirm({ show: true, photoType, photoKey: key });
    };

    const handleDeleteConfirm = async () => {
        if (deleteConfirm.photoType && deleteConfirm.photoKey) {
            await onDeletePhoto(deleteConfirm.photoType, deleteConfirm.photoKey);
        }
        setDeleteConfirm({ show: false, photoType: null, photoKey: null });
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm({ show: false, photoType: null, photoKey: null });
    };

    return (
        <>
            <Card className="guest-photos-section mb-4">
                <Card.Header className="d-flex align-items-center gap-2">
                    <Camera size={18} />
                    <span>Bilder</span>
                    {totalPhotos > 0 && (
                        <span className="badge bg-secondary">{totalPhotos}</span>
                    )}
                </Card.Header>
                <Card.Body>
                    {uploadError && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => setUploadError(null)}
                            className="mb-3"
                        >
                            {uploadError}
                        </Alert>
                    )}
                    {/* Before/progress groups render only when enabled (prop
                        defined). The "after" group always renders — after-photos
                        are the completion evidence and must always be available. */}
                    {beforePhotos !== undefined && (
                        <PhotoGroup
                            title={WORK_ORDER_LABELS.beforePictures}
                            photoType="before"
                            photos={beforePhotos}
                            onUpload={readOnly ? undefined : (file) => handleUpload(file, 'before')}
                            onDeleteClick={(key) => handleDeleteClick('before', key)}
                            onViewClick={handleViewPhoto}
                            onGetDownloadUrl={onGetDownloadUrl}
                            submitting={submitting}
                            isUploading={uploadingPhotoType === 'before'}
                            canDelete={readOnly ? () => false : canDelete}
                        />
                    )}
                    {progressPhotos !== undefined && (
                        <PhotoGroup
                            title={WORK_ORDER_LABELS.progressPictures}
                            photoType="progress"
                            photos={progressPhotos}
                            onUpload={readOnly ? undefined : (file) => handleUpload(file, 'progress')}
                            onDeleteClick={(key) => handleDeleteClick('progress', key)}
                            onViewClick={handleViewPhoto}
                            onGetDownloadUrl={onGetDownloadUrl}
                            submitting={submitting}
                            isUploading={uploadingPhotoType === 'progress'}
                            canDelete={readOnly ? () => false : canDelete}
                        />
                    )}
                    <PhotoGroup
                        title={WORK_ORDER_LABELS.afterPictures}
                        photoType="after"
                        photos={afterPhotos}
                        onUpload={readOnly ? undefined : (file) => handleUpload(file, 'after')}
                        onDeleteClick={(key) => handleDeleteClick('after', key)}
                        onViewClick={handleViewPhoto}
                        onGetDownloadUrl={onGetDownloadUrl}
                        submitting={submitting}
                        isUploading={uploadingPhotoType === 'after'}
                        canDelete={readOnly ? () => false : canDelete}
                    />
                </Card.Body>
            </Card>

            <ConfirmationModal
                show={deleteConfirm.show}
                title="Ta bort bild"
                message="Vill du ta bort denna bild?"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={submitting}
            />

            <PhotoViewerModal
                show={viewerLoading || viewerUrl !== null}
                imageUrl={viewerUrl || ''}
                loading={viewerLoading}
                onClose={handleCloseViewer}
            />
        </>
    );
};

export default GuestPhotosSection;
